import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/Card';
import { ArrowLeft, Save } from 'lucide-react';
import { getPromoCode, updatePromoCode, listRestaurants } from '../services/resortAdminApi';
import { validatePromoCodeForm, getValidationErrorMessage } from '../utils/promoCodeValidation';

interface PromoCodeData {
  name: string;
  code: string;
  discount_type: 'fixed' | 'percentage';
  discount_value: number;
  min_order_amount: number;
  max_order_amount: number;
  restaurant?: string;
  restaurant_name?: string;
  max_uses_per_user: number;
  total_usage_count: number;
  start_date: string;
  end_date: string;
  status: 'Active' | 'Inactive';
  is_redeemed: number;
  redeemed_by_order?: string;
  redeemed_at?: string;
  created_by_admin?: string;
  batch_id?: string;
  creation?: string;
}

interface RestaurantOption {
  name: string;
  restaurant_name: string;
}

export const PromoCodeDetails: React.FC = () => {
  const { name } = useParams<{ name: string }>();
  const navigate = useNavigate();
  const [promoCode, setPromoCode] = useState<PromoCodeData | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [restaurants, setRestaurants] = useState<RestaurantOption[]>([]);

  const [formData, setFormData] = useState({
    discount_type: 'fixed' as 'fixed' | 'percentage',
    discount_value: '' as number | '',
    min_order_amount: 0,
    max_order_amount: 0,
    restaurant: '' as string,
    max_uses_per_user: 0,
    start_date: '',
    end_date: '',
    status: 'Active' as 'Active' | 'Inactive'
  });

  useEffect(() => {
    loadPromoCode();
  }, [name]);

  // Load restaurant options on mount
  useEffect(() => {
    const loadRestaurants = async () => {
      try {
        const result = await listRestaurants(undefined, 1, 200);
        const data = result?.data || result || [];
        setRestaurants(data.map((r: any) => ({ name: r.name, restaurant_name: r.restaurant_name || r.name })));
      } catch (err) {
        console.error('Failed to load restaurants:', err);
      }
    };
    loadRestaurants();
  }, []);

  const loadPromoCode = async () => {
    if (!name) return;

    setLoading(true);
    try {
      const data = await getPromoCode(name);
      setPromoCode(data);
      setFormData({
        discount_type: data.discount_type,
        discount_value: data.discount_value,
        min_order_amount: data.min_order_amount || 0,
        max_order_amount: data.max_order_amount || 0,
        restaurant: data.restaurant || '',
        max_uses_per_user: data.max_uses_per_user || 0,
        start_date: data.start_date,
        end_date: data.end_date,
        status: data.status
      });
    } catch (error: any) {
      setError(error.message || 'Failed to load promo code');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name) return;

    setSaving(true);
    setError('');
    setSuccess('');

    // Frontend validation
    const validationErrors = validatePromoCodeForm(formData, true);
    if (validationErrors.length > 0) {
      setError(getValidationErrorMessage(validationErrors));
      setSaving(false);
      return;
    }

    // At this point, validation passed, so discount_value must be a valid number
    if (formData.discount_value === '' || formData.discount_value === undefined) {
      setError('The discount value is required and cannot be 0');
      setSaving(false);
      return;
    }

    try {
      await updatePromoCode({
        name,
        ...formData,
        discount_value: formData.discount_value as number,
        restaurant: formData.restaurant || '',
      });
      setSuccess('Promo code updated successfully');
      loadPromoCode();
    } catch (error: any) {
      // Extract user-friendly error message
      let errorMessage = 'Failed to update promo code';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?._error_message) {
        errorMessage = error.response.data._error_message;
      } else if (error.response?.data?.exception) {
        errorMessage = error.response.data.exception;
      }
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">Loading...</div>;
  }

  if (!promoCode) {
    return <div className="text-center py-8 text-red-600">Promo code not found</div>;
  }

  const isEditable = !promoCode.is_redeemed || (promoCode.max_uses_per_user > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate('/promo-codes')}
          className="p-2 hover:bg-gray-100 rounded-full"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-800">
            Promo Code: <span className="font-mono text-indigo-600">{promoCode.code}</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            {isEditable ? 'Edit promo code settings' : 'This code has been redeemed and cannot be edited'}
          </p>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4">
          <p className="text-sm text-green-700">{success}</p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Edit Form */}
        <div className="lg:col-span-2">
          <Card>
            <form onSubmit={handleSave} className="space-y-4 p-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Discount Type</label>
                  <select
                    value={formData.discount_type}
                    onChange={(e) => setFormData({ ...formData, discount_type: e.target.value as 'fixed' | 'percentage' })}
                    disabled={!isEditable}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                  >
                    <option value="fixed">Fixed Amount (€)</option>
                    <option value="percentage">Percentage (%)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Value <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.discount_value === '' ? '' : formData.discount_value}
                    onChange={(e) => {
                      const value = e.target.value;
                      setFormData({ 
                        ...formData, 
                        discount_value: value === '' ? '' : parseFloat(value) || ''
                      });
                    }}
                    disabled={!isEditable}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Minimum Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.min_order_amount}
                  onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                  disabled={!isEditable}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Maximum Order Amount</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.max_order_amount}
                  onChange={(e) => setFormData({ ...formData, max_order_amount: parseFloat(e.target.value) || 0 })}
                  disabled={!isEditable}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500">0 = no limit</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Restaurant</label>
                <select
                  value={formData.restaurant}
                  onChange={(e) => setFormData({ ...formData, restaurant: e.target.value })}
                  disabled={!isEditable}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                >
                  <option value="">All restaurants</option>
                  {restaurants.map(r => (
                    <option key={r.name} value={r.name}>{r.restaurant_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Max Uses Per User</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.max_uses_per_user}
                  onChange={(e) => setFormData({ ...formData, max_uses_per_user: parseInt(e.target.value) || 0 })}
                  disabled={!isEditable}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                />
                <p className="mt-1 text-xs text-gray-500">0 = single-use (one use total), 1+ = multi-use per user</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Start Date</label>
                  <input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                    disabled={!isEditable}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">End Date</label>
                  <input
                    type="date"
                    value={formData.end_date}
                    onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                    disabled={!isEditable}
                    className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'Active' | 'Inactive' })}
                  disabled={!isEditable}
                  className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-alps-primary focus:border-alps-primary sm:text-sm disabled:bg-gray-100"
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              {isEditable && (
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-alps-primary text-white rounded-md hover:bg-alps-primary-dark disabled:opacity-50"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                </div>
              )}
            </form>
          </Card>
        </div>

        {/* Info Panel */}
        <div>
          <Card>
            <div className="p-4 space-y-4">
              <h3 className="font-medium text-gray-900">Code Information</h3>
              
              <div>
                <span className="text-sm text-gray-500">Code</span>
                <p className="font-mono font-medium text-indigo-600">{promoCode.code}</p>
              </div>

              <div>
                <span className="text-sm text-gray-500">Status</span>
                <p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    promoCode.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {promoCode.status}
                  </span>
                </p>
              </div>

              <div>
                <span className="text-sm text-gray-500">Restaurant</span>
                <p className="text-sm font-medium">{promoCode.restaurant_name || 'All restaurants'}</p>
              </div>

              {promoCode.max_order_amount > 0 && (
                <div>
                  <span className="text-sm text-gray-500">Max Order Amount</span>
                  <p className="text-sm font-medium">&euro;{promoCode.max_order_amount}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Usage Mode</span>
                <p className="text-sm font-medium">
                  {promoCode.max_uses_per_user > 0
                    ? `Multi-use (${promoCode.max_uses_per_user}x per user)`
                    : 'Single-use'}
                </p>
              </div>

              {promoCode.max_uses_per_user > 0 && (
                <div>
                  <span className="text-sm text-gray-500">Total Uses</span>
                  <p className="text-sm font-medium">{promoCode.total_usage_count}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Redeemed</span>
                <p>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    promoCode.is_redeemed ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {promoCode.is_redeemed ? 'Yes' : 'No'}
                  </span>
                </p>
              </div>

              {promoCode.redeemed_by_order && (
                <div>
                  <span className="text-sm text-gray-500">Used by Order</span>
                  <p className="text-sm font-medium">{promoCode.redeemed_by_order}</p>
                </div>
              )}

              {promoCode.redeemed_at && (
                <div>
                  <span className="text-sm text-gray-500">Redeemed At</span>
                  <p className="text-sm">{new Date(promoCode.redeemed_at).toLocaleString()}</p>
                </div>
              )}

              {promoCode.batch_id && (
                <div>
                  <span className="text-sm text-gray-500">Batch ID</span>
                  <p className="text-sm font-mono">{promoCode.batch_id}</p>
                </div>
              )}

              <div>
                <span className="text-sm text-gray-500">Created</span>
                <p className="text-sm">{promoCode.creation ? new Date(promoCode.creation).toLocaleString() : '-'}</p>
              </div>

              {promoCode.created_by_admin && (
                <div>
                  <span className="text-sm text-gray-500">Created By</span>
                  <p className="text-sm">{promoCode.created_by_admin}</p>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

